/**
 * DocumentChangeViewer - Complete Google Docs-style change tracking system
 * 
 * Combines VisualChangeTracker and DocumentChangeOverlay for a complete
 * change tracking experience similar to Google Docs version history.
 */

import React, { useState, useCallback } from 'react';
import { VisualChangeTracker, type VisualChange } from './VisualChangeTracker';
import { DocumentChangeOverlay } from './DocumentChangeOverlay';

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

// VisualChange interface is now imported from VisualChangeTracker

interface DocumentChangeViewerProps {
  documentId: string;
  versionId?: string;
  documentContent: string;
  documentTitle?: string;
  className?: string;
  onClose?: () => void;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const DocumentChangeViewer: React.FC<DocumentChangeViewerProps> = ({
  documentId,
  versionId,
  documentContent,
  documentTitle = 'Document',
  className = '',
  onClose
}) => {
  // State
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedChangeId, setSelectedChangeId] = useState<string | null>(null);
  const [changes, setChanges] = useState<VisualChange[]>([]);

  // Handle changes loaded from tracker
  const handleChangesLoaded = useCallback((loadedChanges: VisualChange[]) => {
    setChanges(loadedChanges);
  }, []);

  // Handle change selection from tracker
  const handleChangeSelection = useCallback((changeId: string | null) => {
    setSelectedChangeId(changeId);
  }, []);

  // Handle user selection from tracker
  const handleUserSelection = useCallback((userId: string | null) => {
    setSelectedUserId(userId);
    setSelectedChangeId(null); // Reset change selection when user changes
  }, []);

  // Handle change click from overlay
  const handleChangeClick = useCallback((changeId: string) => {
    setSelectedChangeId(changeId);
  }, []);

  return (
    <div className={`flex h-full ${className}`}>
      {/* Left Panel - Change Tracker */}
      <div className="w-80 flex-shrink-0 border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Version History
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {documentTitle}
                </p>
              </div>
              {onClose && (
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Change Tracker */}
          <div className="flex-1 overflow-y-auto">
            <VisualChangeTracker
              documentId={documentId}
              versionId={versionId}
              documentContent={documentContent}
              onChangeSelection={handleChangeSelection}
              onUserSelection={handleUserSelection}
              onChangesLoaded={handleChangesLoaded}
              className="border-0 rounded-none"
            />
          </div>
        </div>
      </div>

      {/* Right Panel - Document with Change Overlay */}
      <div className="flex-1 flex flex-col bg-white dark:bg-gray-800">
        {/* Document Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Document Preview
              </h3>
              {selectedUserId && (
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Showing changes by selected user
                </p>
              )}
            </div>
            
            {/* View Controls */}
            <div className="flex items-center space-x-2">
              {selectedUserId && (
                <button
                  onClick={() => handleUserSelection(null)}
                  className="px-3 py-1 text-sm bg-lex-accent-soft text-lex-accent rounded-md hover:bg-lex-state-hover"
                >
                  Show All Changes
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Document Content with Overlay */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-4xl mx-auto">
            <DocumentChangeOverlay
              documentContent={documentContent}
              changes={changes}
              selectedUserId={selectedUserId}
              selectedChangeId={selectedChangeId}
              onChangeClick={handleChangeClick}
              className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MODAL WRAPPER
// ============================================================================

interface DocumentChangeViewerModalProps extends DocumentChangeViewerProps {
  isOpen: boolean;
  onClose: () => void;
}

export const DocumentChangeViewerModal: React.FC<DocumentChangeViewerModalProps> = ({
  isOpen,
  onClose,
  ...props
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative h-full flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-7xl h-full max-h-[90vh] overflow-hidden">
          <DocumentChangeViewer
            {...props}
            onClose={onClose}
            className="h-full"
          />
        </div>
      </div>
    </div>
  );
};

export default DocumentChangeViewer;
